import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  Save,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useApp } from "../domain/AppContext";
import { formatClassName } from "../domain/classCode";
import {
  canStudentEnroll,
  isPaidOlympiad,
  resolvePhaseFee,
} from "../domain/rules";
import type { Enrollment, Olympiad, TeamMember } from "../domain/types";
import { Button, Dialog, Field, InlineMessage } from "../components/ui";
import { remoteRepository } from "../services/remoteRepository";

const steps = [
  "Confirmar dados pessoais",
  "Verificar elegibilidade",
  "Informações adicionais",
  "Termos necessários",
  "Revisar inscrição",
  "Confirmar",
];

interface EnrollmentDraft {
  shift: string;
  accessibility: string;
  notes: string;
  accepted: boolean;
  teamName: string;
  teamMemberIds: string[];
  requestId: string;
}

const defaultDraft: EnrollmentDraft = {
  shift: "Tarde · 14h às 17h",
  accessibility: "Não",
  notes: "",
  accepted: false,
  teamName: "",
  teamMemberIds: [],
  requestId: "",
};

export function EnrollmentWizardPage() {
  const { slug } = useParams();
  const { profile, data, createEnrollment, notify } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<EnrollmentDraft>(() => ({ ...defaultDraft, requestId: crypto.randomUUID() }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<Enrollment | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState<'pending' | 'saving' | 'saved' | 'error'>('pending');
  const [discardFailed, setDiscardFailed] = useState(false);
  const finalizing = useRef(false);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const olympiad = data?.olympiads.find((item) => item.slug === slug);
  const student = data?.students.find((item) => item.id === profile?.id);
  const draftKey = olympiad ? `enrollment.${olympiad.id}` : "";

  useEffect(() => {
    if (!draftKey) return;
    let current = true;
    setDraftLoaded(false);
    void remoteRepository.getApplicationRecord<EnrollmentDraft>('enrollment_draft', draftKey).then((result) => {
      if (!current) return;
      if (!result.ok) { setError('Não foi possível carregar seu rascunho. Recarregue antes de editar.'); return; }
      if (result.data) setDraft({ ...defaultDraft, ...result.data, requestId: result.data.requestId || crypto.randomUUID() });
      setDraftLoaded(true);
    });
    return () => { current = false; };
  }, [draftKey]);

  const saveDraft = () => {
    const pending = saveQueue.current.then(async () => {
      if (finalizing.current) return null;
      setDraftSaveStatus('saving');
      const result = await remoteRepository.saveApplicationRecord('enrollment_draft', draftKey, draft, profile?.unitId);
      setDraftSaveStatus(result.ok ? 'saved' : 'error');
      if (result.ok) setError((current) => current.startsWith('Falha ao salvar o rascunho.') ? '' : current);
      return result;
    });
    saveQueue.current = pending;
    return pending;
  };
  useEffect(() => {
    if (!draftLoaded || !draftKey || created || submitting) return;
    setDraftSaveStatus('pending');
    const timer = window.setTimeout(() => {
      void saveDraft().then((result) => { if (result && !result.ok) setError('Falha ao salvar o rascunho. Seus dados continuam nesta tela; tente salvar novamente.'); });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [draftLoaded, created, draft, draftKey, submitting]);

  useEffect(() => {
    const heading = document.querySelector<HTMLElement>(".wizard-section h1");
    if (!heading) return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }, [step]);

  if (!profile || profile.role !== "student")
    return <Navigate to="/dashboard" replace />;
  if (!data)
    return <div className="app-bootstrap" role="status"><div className="bootstrap-spinner" /><p>Carregando dados da inscrição...</p></div>;
  if (!olympiad) return <InlineMessage type="error" title="Olimpíada não encontrada"><Link to="/olimpiadas">Voltar ao catálogo</Link></InlineMessage>;
  if (!student || !canStudentEnroll(olympiad, student) || (olympiad.status !== "open" && !created))
    return <InlineMessage type="error" title="Inscrição indisponível">Esta olimpíada não está disponível para a sua turma. <Link to={`/olimpiadas/${olympiad.slug}`}>Ver detalhes</Link></InlineMessage>;
  const existing = data.enrollments.find(
    (item) =>
      item.olympiadId === olympiad.id &&
      item.studentIds.includes(profile.id) &&
      item.status !== "cancelled",
  );
  if (existing && !created)
    return <InlineMessage title="Você já está inscrito">Acompanhe a situação na página da olimpíada. <Link to={`/olimpiadas/${olympiad.slug}`}>Ver minha inscrição</Link></InlineMessage>;

  const canContinue = step !== 3 || draft.accepted;
  const progress = Math.round(((step + 1) / steps.length) * 100);

  const next = () => {
    setError("");
    if (!canContinue) {
      setError("Leia e aceite o regulamento para continuar.");
      return;
    }
    if (step === 2 && olympiad.format === 'Equipe') {
      const total = draft.teamMemberIds.length + 1;
      const minimum = olympiad.teamMin ?? 2;
      const maximum = olympiad.variableTeamSize ? olympiad.teamMax ?? minimum : minimum;
      if (!draft.teamName.trim() || total < minimum || total > maximum) { setError(`Informe o nome e selecione entre ${minimum} e ${maximum} integrantes, contando você.`); return; }
    }
    if (step < 4) setStep((current) => current + 1);
  };

  const confirm = async () => {
    if (submitting || finalizing.current || !draftLoaded || !draft.accepted) return;
    finalizing.current = true;
    setError("");
    setSubmitting(true);
    await saveQueue.current;
    const result = await createEnrollment(olympiad.id, draft.teamName, draft.teamMemberIds, {
      shift: draft.shift, accessibility: draft.accessibility === 'Sim' ? 'Sim' : 'Não',
      notes: draft.notes, accepted: draft.accepted, termsVersion: 'enrollment-v1',
    }, draft.requestId);
    setSubmitting(false);
    if (result.ok) {
      const discarded = await remoteRepository.deleteApplicationRecord('enrollment_draft', draftKey);
      setDiscardFailed(!discarded.ok);
      setCreated(result.data);
      setStep(5);
      notify({
        type: "success",
        title:
          result.data.status === "confirmed"
            ? "Inscrição gratuita confirmada"
            : "Inscrição criada, pagamento pendente",
      });
    } else { finalizing.current = false; setError(result.error.message); }
  };

  if (created) {
    return (
      <div className="enrollment-success">
        <div className="success-mark">
          <Check size={34} />
        </div>
        <p className="eyebrow">
          {created.status === "confirmed"
            ? "Participação confirmada"
            : "Pagamento necessário"}
        </p>
        <h1>
          {created.status === "confirmed"
            ? "Sua inscrição está confirmada!"
            : "Inscrição criada com sucesso"}
        </h1>
        <p>Você solicitou participação na {olympiad.name} 2026.</p>
        <div className="protocol-card">
          <span>Protocolo</span>
          <strong>{created.protocol}</strong>
          <p>
            Situação:{" "}
            {created.status === "confirmed"
              ? "confirmada automaticamente, olimpíada gratuita"
              : `aguardando pagamento de ${created.payments?.[0]?.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`}
          </p>
        </div>
        {discardFailed ? <InlineMessage type="error" title="Inscrição persistida; descarte do rascunho não confirmado">
          A inscrição já foi registrada. Tente apenas remover o rascunho, sem repetir a inscrição.
          <Button variant="secondary" onClick={async () => { const result = await remoteRepository.deleteApplicationRecord('enrollment_draft', draftKey); setDiscardFailed(!result.ok); }}>Tentar descartar rascunho</Button>
        </InlineMessage> : null}
        <div className="success-next-steps">
          <h2>Próximos passos</h2>
          {created.status === "confirmed" ? (
            <ol>
              <li>
                <span>1</span>
                <div>
                  <strong>Participação garantida</strong>
                  <p>Não é necessária aprovação manual.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Confira o calendário</strong>
                  <p>Acompanhe datas, horário e local da prova.</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Prepare-se para a prova</strong>
                  <p>Confira o cronograma e o regulamento.</p>
                </div>
              </li>
            </ol>
          ) : (
            <ol>
              <li>
                <span>1</span>
                <div>
                  <strong>Realize o pagamento</strong>
                  <p>Siga as orientações financeiras fornecidas pela escola.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Aguarde a validação</strong>
                  <p>
                    Ao registrar o pagamento, a inscrição é confirmada
                    automaticamente.
                  </p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Acompanhe as próximas fases</strong>
                  <p>
                    Novas parcelas podem ser abertas conforme a classificação.
                  </p>
                </div>
              </li>
            </ol>
          )}
        </div>
        {olympiad.continuedEnrollmentEnabled && olympiad.continuedEnrollmentUrl ? (
          <div className="continued-enrollment-callout">
            <div>
              <strong>Conclua a etapa externa</strong>
              <p>Seu protocolo no Oficina Sapiens já foi criado. Agora finalize o cadastro solicitado pela organização.</p>
            </div>
            <a className="button button-primary" href={olympiad.continuedEnrollmentUrl} target="_blank" rel="noopener noreferrer">
              Continuar inscrição <ExternalLink size={16} />
            </a>
          </div>
        ) : null}
        <div className="success-actions">
          <Button onClick={() => navigate(`/olimpiadas/${olympiad.slug}`)}>
            Ver minha inscrição
          </Button>
          <Button variant="secondary" onClick={() => navigate("/olimpiadas")}>
            Voltar para Olimpíadas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="enrollment-wizard-page">
      <header className="wizard-header">
        <Link to={`/olimpiadas/${olympiad.slug}`}>
          <ArrowLeft size={18} /> {olympiad.acronym} 2026
        </Link>
        <div>
          <span aria-live="polite">{draftSaveStatus === 'saved' ? 'Rascunho privado salvo' : draftSaveStatus === 'saving' ? 'Salvando rascunho privado...' : draftSaveStatus === 'error' ? 'Rascunho não salvo' : 'Alterações aguardando salvamento'}</span>
          <button
            type="button"
            onClick={() => navigate(`/olimpiadas/${olympiad.slug}`)}
            aria-label="Sair da inscrição"
          >
            <X size={19} />
          </button>
        </div>
      </header>
      <div className="mobile-progress">
        <p>
          Etapa {step + 1} de 6 · {steps[step]}
        </p>
        <div>
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="wizard-layout">
        <aside className="wizard-steps">
          <p className="eyebrow">Seu progresso</p>
          <h2>Inscrição · {olympiad.acronym}</h2>
          <ol>
            {steps.map((label, index) => (
              <li
                key={label}
                className={
                  index === step ? "active" : index < step ? "done" : ""
                }
              >
                <span>{index < step ? <Check size={14} /> : index + 1}</span>
                <div>
                  <strong>{label}</strong>
                  <small>
                    {index < step
                      ? "Concluído"
                      : index === step
                        ? "Em andamento"
                        : ""}
                  </small>
                </div>
              </li>
            ))}
          </ol>
          <div className="wizard-help">
            <HelpCircle size={20} />
            <div>
              <strong>Precisa de ajuda?</strong>
              <p>A coordenação responde em até um dia útil.</p>
              <button type="button" onClick={() => setHelpOpen(true)}>Abrir central de ajuda</button>
            </div>
          </div>
        </aside>
        <main className="wizard-main">
          <p className="eyebrow">Etapa {step + 1} de 6</p>
          {step === 0 ? <PersonalData /> : null}
          {step === 1 ? <Eligibility olympiadName={olympiad.acronym} /> : null}
          {step === 2 ? (
            <AdditionalInfo
              draft={draft}
              setDraft={setDraft}
              olympiad={olympiad}
            />
          ) : null}
          {step === 3 ? (
            <Terms
              olympiadName={olympiad.name}
              paid={isPaidOlympiad(olympiad)}
              accepted={draft.accepted}
              onChange={(accepted) =>
                setDraft((current) => ({ ...current, accepted }))
              }
            />
          ) : null}
          {step === 4 ? (
            <Review
              draft={draft}
              olympiadName={`${olympiad.acronym} 2026`}
              payment={
                isPaidOlympiad(olympiad)
                  ? resolvePhaseFee(olympiad, student, olympiad.phases[0])
                  : 0
              }
              paymentSchedule={isPaidOlympiad(olympiad) ? (olympiad.feeVariationByPhase ? olympiad.phases : olympiad.phases.slice(0, 1)).map((phase) => ({ title: phase.title, amount: resolvePhaseFee(olympiad, student, phase) })) : []}
              paymentScope={olympiad.format === "Equipe" ? "por equipe" : "por estudante"}
              onEdit={setStep}
            />
          ) : null}
          {error ? <InlineMessage type="error" title={error} /> : null}
          <footer className="wizard-actions">
            <Button
              variant="ghost"
              disabled={step === 0 || submitting || !draftLoaded}
              onClick={() => setStep((current) => Math.max(0, current - 1))}
            >
              <ArrowLeft size={17} /> Voltar
            </Button>
            <button
              className="save-draft"
              type="button"
              disabled={!draftLoaded || submitting}
              onClick={async () => {
                const result = await saveDraft();
                notify(result?.ok ? { type: 'success', title: 'Rascunho privado salvo' } : { type: 'error', title: 'Rascunho não salvo. Tente novamente.' });
              }}
            >
              <Save size={16} /> Salvar e continuar depois
            </button>
            {step < 4 ? (
              <Button disabled={!draftLoaded || submitting} onClick={next}>
                Continuar <ArrowRight size={17} />
              </Button>
            ) : (
              <Button loading={submitting} onClick={confirm}>
                Confirmar inscrição
              </Button>
            )}
          </footer>
        </main>
      </div>
      <Dialog open={helpOpen} title="Ajuda com a inscrição" description="Você pode sair e continuar depois sem perder o rascunho." onClose={() => setHelpOpen(false)} actions={<Button onClick={() => setHelpOpen(false)}>Entendi</Button>}><div className="help-guides"><section><strong>Antes de confirmar</strong><ol><li>Confira seus dados e a turma.</li><li>Em equipe, escolha somente colegas elegíveis e ainda não inscritos.</li><li>Leia o regulamento e revise cobranças por fase.</li></ol></section><section><strong>Depois de confirmar</strong><ol><li>Gratuitas são confirmadas automaticamente.</li><li>Pagas aguardam o registro da parcela.</li><li>Novas parcelas só aparecem se houver classificação para outra fase.</li></ol></section></div></Dialog>
    </div>
  );
}

function PersonalData() {
  const { profile, data } = useApp();
  const student = data?.students.find((item) => item.id === profile?.id);
  return (
    <section className="wizard-section">
      <h1>Confirme seus dados pessoais</h1>
      <p>
        Esses dados vêm do cadastro da escola. Se algo estiver incorreto, peça a
        atualização antes de continuar.
      </p>
      <div className="review-card">
        <div>
          <span>Nome completo</span>
          <strong>{student?.name}</strong>
        </div>
        <div>
          <span>RA</span>
          <strong>{student?.ra}</strong>
        </div>
        <div>
          <span>Turma</span>
          <strong>{formatClassName(student?.className)}</strong>
        </div>
        <div>
          <span>Data de nascimento</span>
          <strong>{student?.birthDate.split("-").reverse().join("/")}</strong>
        </div>
        <div>
          <span>E-mail</span>
          <strong>{student?.email}</strong>
        </div>
        <div>
          <span>Responsável</span>
          <strong>{student?.guardian}</strong>
        </div>
      </div>
      <InlineMessage title="Exposição mínima de dados">
        Somente nome, série e data de nascimento serão compartilhados com a
        organização quando necessário.
      </InlineMessage>
    </section>
  );
}

function Eligibility({ olympiadName }: { olympiadName: string }) {
  return (
    <section className="wizard-section">
      <h1>Você pode participar</h1>
      <p>
        Conferimos os requisitos antes de pedir qualquer informação adicional.
      </p>
      <div className="eligibility-list">
        <article>
          <CheckCircle2 />
          <div>
            <strong>Matrícula ativa</strong>
            <p>Cadastro escolar confirmado.</p>
          </div>
        </article>
        <article>
          <CheckCircle2 />
          <div>
            <strong>Série elegível</strong>
            <p>Sua turma participa da {olympiadName}.</p>
          </div>
        </article>
        <article>
          <CheckCircle2 />
          <div>
            <strong>Dados completos</strong>
            <p>As informações obrigatórias estão preenchidas.</p>
          </div>
        </article>
        <article>
          <CheckCircle2 />
          <div>
            <strong>Sem inscrição duplicada</strong>
            <p>Nenhuma solicitação ativa foi encontrada.</p>
          </div>
        </article>
      </div>
    </section>
  );
}

function AdditionalInfo({
  draft,
  setDraft,
  olympiad,
}: {
  draft: EnrollmentDraft;
  setDraft: React.Dispatch<React.SetStateAction<EnrollmentDraft>>;
  olympiad: Olympiad;
}) {
  const { profile } = useApp();
  const team = olympiad.format === 'Equipe';
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersError, setMembersError] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  useEffect(() => {
    if (!team) return;
    let current = true; setLoadingMembers(true);
    void remoteRepository.getTeamMembers(olympiad.id).then((result) => {
      if (!current) return;
      setLoadingMembers(false);
      if (result.ok) setMembers(result.data); else setMembersError(result.error.message);
    });
    return () => { current = false; };
  }, [team, olympiad.id, profile?.unitId]);
  const eligibleMembers = members;
  const toggleMember = (id: string) => setDraft((current) => ({ ...current, teamMemberIds: current.teamMemberIds.includes(id) ? current.teamMemberIds.filter((item) => item !== id) : [...current.teamMemberIds, id] }));
  return (
    <section className="wizard-section">
      <h1>Informações adicionais</h1>
      <p>
        A organização solicita estes dados para preparar a aplicação da prova.
      </p>
      <div className="form-grid">
        {team ? (
          <>
          <Field
            label="Nome da equipe"
            hint="Use um nome respeitoso, com até 50 caracteres."
          >
            <input
              value={draft.teamName}
              maxLength={50}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  teamName: event.target.value,
                }))
              }
            />
          </Field>
          <Field label="Integrantes da equipe" hint={`Você já conta como integrante. Selecione ${Math.max(0, (olympiad.teamMin ?? 2) - 1)} a ${Math.max(0, (olympiad.variableTeamSize ? olympiad.teamMax ?? olympiad.teamMin ?? 2 : olympiad.teamMin ?? 2) - 1)} colega(s).`}>
            <div className="team-member-picker">{loadingMembers ? <p role="status">Carregando colegas elegíveis...</p> : membersError ? <InlineMessage type="error" title="Não foi possível carregar os colegas">{membersError}</InlineMessage> : eligibleMembers.length ? eligibleMembers.map((member) => <label key={member.id} className={draft.teamMemberIds.includes(member.id) ? 'selected' : ''}><input type="checkbox" checked={draft.teamMemberIds.includes(member.id)} onChange={() => toggleMember(member.id)} /><span className="avatar" aria-hidden="true">{member.name.split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span><div><strong>{member.name}</strong><small>{formatClassName(member.className)}</small></div></label>) : <InlineMessage title="Nenhum colega elegível disponível">Todos os colegas elegíveis já estão inscritos ou não há outra turma habilitada.</InlineMessage>}</div>
          </Field>
          </>
        ) : null}
        <Field label="Turno de preferência">
          <select
            value={draft.shift}
            onChange={(event) =>
              setDraft((current) => ({ ...current, shift: event.target.value }))
            }
          >
            <option>Tarde · 14h às 17h</option>
            <option>Manhã · 9h às 12h</option>
          </select>
        </Field>
        <Field label="Precisa de atendimento especializado?">
          <div className="binary-options">
            <button
              type="button"
              aria-label="Sim"
              aria-pressed={draft.accessibility === "Sim"}
              className={draft.accessibility === "Sim" ? "active" : ""}
              onClick={() =>
                setDraft((current) => ({ ...current, accessibility: "Sim" }))
              }
            >
              Sim
            </button>
            <button
              type="button"
              aria-label="Não"
              aria-pressed={draft.accessibility === "Não"}
              className={draft.accessibility === "Não" ? "active" : ""}
              onClick={() =>
                setDraft((current) => ({ ...current, accessibility: "Não" }))
              }
            >
              Não
            </button>
          </div>
        </Field>
        <Field label="Observações para a coordenação" hint="Opcional">
          <textarea
            rows={4}
            maxLength={2000}
            value={draft.notes}
            onChange={(event) =>
              setDraft((current) => ({ ...current, notes: event.target.value }))
            }
            placeholder="Informe uma necessidade ou contexto relevante."
          />
        </Field>
      </div>
    </section>
  );
}

function Terms({
  olympiadName,
  paid,
  accepted,
  onChange,
}: {
  olympiadName: string;
  paid: boolean;
  accepted: boolean;
  onChange: (value: boolean) => void;
}) {
  const [regulationOpen, setRegulationOpen] = useState(false);
  return (
    <section className="wizard-section">
      <h1>Termos necessários</h1>
      <p>Leia o resumo e consulte o regulamento completo antes de continuar.</p>
      <div className="terms-card">
        <h2>Como seus dados serão usados</h2>
        <ul>
          <li>
            A escola usa os dados para validar a inscrição e organizar a prova.
          </li>
          <li>
            A organização recebe apenas os dados necessários para registrar a
            participação.
          </li>
          <li>
            {paid
              ? "A confirmação ocorrerá após o registro do pagamento."
              : "A confirmação será automática porque a olimpíada é gratuita."}
          </li>
          <li>O histórico da operação fica registrado para auditoria.</li>
        </ul>
        <button type="button" onClick={() => setRegulationOpen(true)}>Abrir regulamento completo</button>
      </div>
      <label className="acceptance-check">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          <Check size={15} />
        </span>
        <strong>
          Li e aceito o regulamento da {olympiadName} e autorizo o envio dos
          dados necessários.
        </strong>
      </label>
      <Dialog open={regulationOpen} title={`Resumo do regulamento · ${olympiadName}`} description="Consulte também o documento oficial disponibilizado pela organização." onClose={() => setRegulationOpen(false)} actions={<Button onClick={() => setRegulationOpen(false)}>Concluir leitura</Button>}><div className="terms-card"><h2>Condições de participação</h2><ul><li>A inscrição está vinculada aos dados escolares e à turma elegível.</li><li>Datas e local aparecem na página da olimpíada.</li><li>Em equipes, cada integrante só pode participar de uma equipe ativa.</li><li>{paid ? 'Cada cobrança exigível deve ser quitada para confirmar a etapa correspondente.' : 'Não há cobrança e a confirmação ocorre automaticamente.'}</li><li>Classificações e resultados só aparecem após publicação pela escola.</li></ul></div><InlineMessage title="Documento oficial">Este resumo orienta o fluxo da plataforma. Regras específicas da organização prevalecem quando o arquivo oficial estiver anexado.</InlineMessage></Dialog>
    </section>
  );
}

function Review({
  draft,
  olympiadName,
  payment,
  paymentSchedule,
  paymentScope,
  onEdit,
}: {
  draft: EnrollmentDraft;
  olympiadName: string;
  payment: number;
  paymentSchedule: { title: string; amount: number }[];
  paymentScope: string;
  onEdit: (step: number) => void;
}) {
  const { profile } = useApp();
  return (
    <section className="wizard-section">
      <h1>Revise sua inscrição</h1>
      <p>Confira as informações antes de confirmar.</p>
      <div className="review-card editable">
        <div>
          <span>Aluna</span>
          <strong>
            {profile?.name} · {formatClassName(profile?.className)}
          </strong>
          <button type="button" onClick={() => onEdit(0)}>
            Editar
          </button>
        </div>
        <div>
          <span>Olimpíada</span>
          <strong>{olympiadName}</strong>
        </div>
        <div>
          <span>Confirmação</span>
          <strong>
            {payment > 0
              ? `Após pagamento de ${payment.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
              : "Automática, sem custo"}
          </strong>
        </div>
        {paymentSchedule.length > 1 ? <div>
          <span>Cobranças previstas</span>
          <strong>{paymentScope}</strong>
          <ul className="compact-list">
            {paymentSchedule.map((item) => <li key={item.title}>{item.title}: {item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</li>)}
          </ul>
        </div> : null}
        {draft.teamName ? (
          <div>
            <span>Equipe</span>
            <strong>{draft.teamName}</strong>
            <button type="button" onClick={() => onEdit(2)}>
              Editar
            </button>
          </div>
        ) : null}
        {draft.teamMemberIds.length ? <div><span>Integrantes adicionais</span><strong>{draft.teamMemberIds.length} colega(s) selecionado(s)</strong><button type="button" onClick={() => onEdit(2)}>Editar</button></div> : null}
        <div>
          <span>Turno da prova</span>
          <strong>{draft.shift}</strong>
          <button type="button" onClick={() => onEdit(2)}>
            Editar
          </button>
        </div>
        <div>
          <span>Atendimento especializado</span>
          <strong>{draft.accessibility}</strong>
          <button type="button" onClick={() => onEdit(2)}>
            Editar
          </button>
        </div>
        <div><span>Observações para a coordenação</span><strong>{draft.notes || 'Nenhuma'}</strong><button type="button" onClick={() => onEdit(2)}>Editar</button></div>
        <div>
          <span>Regulamento</span>
          <strong>{draft.accepted ? "Aceito" : "Pendente"}</strong>
          <button type="button" onClick={() => onEdit(3)}>
            Editar
          </button>
        </div>
      </div>
    </section>
  );
}
